import React, { createContext, useState, useContext, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
// import '../services/api'; // Assumindo que este arquivo configura a instância global do axios
import { useNavigate } from 'react-router-dom';
import { useAlertModal } from '../AlertModalContext'; // NOVO: Importar o hook do modal de alerta

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState(() => {
    return {
      isAuthenticated: false,
      user: null,
      token: null,
      roles: [],
      entidade: null,
      onboarding: { step: null, complete: false },
      subscription: null,
      subscriptionStatus: null,
      isFetchingSubscription: true,
      loginDateTime: null,
      // --- NOVOS ESTADOS PARA PERSONIFICAÇÃO ---
      isImpersonating: false,
      originalAuthState: null,
    };
  });

  // Função para completar um passo do onboarding
    const completeOnboardingStep = async (stepKeyCompleted) => {
        try {
            const response = await axios.post('/api/onboarding/complete-step', { step_completed: stepKeyCompleted });

            // Verificação crucial: Garanta que as chaves corretas (snake_case) estão sendo usadas.
            if (response && response.data &&
                typeof response.data.onboarding_complete !== 'undefined' &&
                typeof response.data.next_step_key !== 'undefined') {

                // Atualiza o authState com o novo status de onboarding
                // A API agora retorna onboarding_complete e next_step_key diretamente, não um objeto 'onboarding' aninhado.
                // O estado de onboarding no authState precisa ser atualizado com base nisso.
                setAuthState(prevState => ({
                    ...prevState,
                    onboarding: {
                        ...prevState.onboarding, // Mantém outros dados de onboarding se houver
                        complete: response.data.onboarding_complete,
                        step: response.data.next_step_key // Atualiza o step para o próximo passo pendente
                    }
                }));
                // Retorna os dados para a página que chamou
                return response.data;
            } else {
                throw new Error("Resposta inválida da API ao completar passo do onboarding.");
            }
        } catch (error) {
            if (error.response) {
            } else if (error.request) {
                console.error(`AuthContext: Sem resposta da API ao completar passo (${stepKeyCompleted}):`, error.request);
            } else {
                console.error(`AuthContext: Erro ao configurar requisição para completar passo (${stepKeyCompleted}):`, error.message);
            }
            if (error.message !== 'Resposta inválida da API ao completar passo do onboarding.') {
                 console.error(`AuthContext: Erro genérico ao completar passo do onboarding (${stepKeyCompleted}):`, error.message);
            }
            throw error; // Re-throw para a página de onboarding tratar
        }
    };


  const navigate = useNavigate();
  const { showAlertModal } = useAlertModal(); // NOVO: Obter a função para mostrar o modal

  const fetchAndUpdateSubscription = useCallback(async ({ silent = false } = {}) => {
    if (!authState.isAuthenticated) {
      setAuthState(prevState => ({ ...prevState, isFetchingSubscription: false }));
      return;
    }

    if (!silent) setAuthState(prevState => ({ ...prevState, isFetchingSubscription: true }));
    try {
      const response = await axios.get('/api/subscription');
      const subscriptionData = response.data;
      const status = subscriptionData?.status || null;

      setAuthState(prevState => {
        const unchanged = prevState.subscriptionStatus === status
          && JSON.stringify(prevState.subscription) === JSON.stringify(subscriptionData);
        if (silent && unchanged && !prevState.isFetchingSubscription) return prevState;
        return {
          ...prevState,
          subscription: subscriptionData,
          subscriptionStatus: status,
          isFetchingSubscription: false,
        };
      });
    } catch (error) {
      const status = error.response?.status === 404 ? 'not_found' : 'error';
      if (silent) return;
      console.error("Erro ao buscar status da assinatura:", error);
      setAuthState(prevState => ({
        ...prevState,
        subscription: null,
        subscriptionStatus: status,
        isFetchingSubscription: false,
      }));
    }
  }, [authState.isAuthenticated]);

  // NOVA FUNÇÃO: Atualiza os dados da assinatura no estado global
  const updatePartialSubscription = useCallback((partialData) => {
    setAuthState(prevAuthState => {
      // Garante que não tentemos mesclar com uma assinatura nula
      if (!prevAuthState.subscription) {
        return prevAuthState;
      }
      // Retorna o novo estado com a assinatura mesclada corretamente.
      // O operador spread (...) garante que qualquer chave em partialData (como current_month_appointments)
      // seja adicionada ou sobrescreva a chave correspondente no objeto de assinatura.
      return {
        ...prevAuthState,
        subscription: { ...prevAuthState.subscription, ...partialData },
      };
    });
  }, []);

  // Função para atualizar todo o objeto de assinatura, usada pelo Dashboard
  const setSubscription = useCallback((subscriptionData) => {
    setAuthState(prevState => ({
      ...prevState,
      subscription: subscriptionData,
      subscriptionStatus: subscriptionData?.status || prevState.subscriptionStatus,
    }));
  }, []);

  const processSuccessfulLogin = useCallback((apiResponseData) => {
    const { user, token, roles, entidade, onboarding } = apiResponseData;    
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // --- CORREÇÃO: Verifica se o usuário é um 'super_admin' ---
    // Se for, a busca de assinatura não é necessária.
    const isSuperAdmin = (roles || []).includes('super_admin');

    setAuthState({
      isAuthenticated: true,
      user,
      token,
      roles: roles || [],
      entidade,
      onboarding,
      // Se for admin, a assinatura já é definida como 'n/a' e a busca não é acionada.
      subscription: isSuperAdmin ? { status: 'n/a' } : null, // Correto: admin não tem assinatura.
      subscriptionStatus: isSuperAdmin ? 'n/a' : null,
      // CORREÇÃO: Inicia como 'false' para todos. O useEffect decidirá se a busca é necessária.
      isFetchingSubscription: false, 
      loginDateTime: new Date(),
      // Não resetamos isImpersonating ou originalAuthState aqui
    });

    // A busca da assinatura agora é controlada exclusivamente pelo useEffect abaixo,
    // que não será acionado para o super_admin devido ao estado inicial definido acima.
    return apiResponseData;
  }, []); // Removido fetchAndUpdateSubscription das dependências

  const login = useCallback(async (email, password) => {
    const response = await axios.post('/api/login', { email, password });
    if (response.data && response.data.token) {
      return processSuccessfulLogin(response.data);
    }
    throw new Error(response.data?.message || 'Resposta inválida do servidor de login.');
  }, [processSuccessfulLogin]);

  const adminLogin = useCallback(async (email, password) => {
    // Chama a nova rota específica para o login do admin
    const response = await axios.post('/api/admin/login', { email, password });
    if (response.data && response.data.token) {
      return processSuccessfulLogin(response.data);
    }
    throw new Error(response.data?.message || 'Resposta inválida do servidor de login de admin.');
  }, [processSuccessfulLogin]);

  const startImpersonation = useCallback(async (impersonatedUserData) => {
    // 1. Salva o estado atual do admin antes de qualquer modificação.
    const originalState = { ...authState };

    // 2. Processa os dados do usuário personificado.
    const { user, token, roles, entidade, onboarding } = impersonatedUserData;
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // 3. Define o novo estado de autenticação de uma só vez.
    // Esta ação irá disparar o useEffect abaixo para buscar a assinatura.
    setAuthState({
      ...authState, // Mantém quaisquer outros estados que não estão sendo alterados.
      isAuthenticated: true,
      user,
      token,
      roles,
      entidade,
      onboarding,
      isImpersonating: true,
      originalAuthState: originalState,
      // Reseta o estado da assinatura para forçar uma nova busca pelo useEffect.
      subscription: null,
      subscriptionStatus: null,
      isFetchingSubscription: false, // Importante: Inicia como false para que o useEffect possa ativá-lo.
    });
  }, [authState]); // Depende do authState para criar o backup.

  const stopImpersonation = useCallback(() => {
    // Restaura o estado original do admin que foi salvo.
    if (authState.originalAuthState) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authState.originalAuthState.token}`;
      setAuthState(authState.originalAuthState);
    } else {
      // Fallback de segurança: se o estado original não for encontrado, faz logout.
      handleLogout();
    }
  }, [authState.originalAuthState]);

  const handleLogout = useCallback(() => {
    delete axios.defaults.headers.common['Authorization'];
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      roles: [],
      entidade: null,
      onboarding: { step: null, complete: false },
      subscription: null,
      subscriptionStatus: null,
      isFetchingSubscription: false,
      loginDateTime: null,
      isImpersonating: false,
      originalAuthState: null,
    });
    navigate('/login');
  }, [navigate]);

  // Efeito para buscar a assinatura quando o usuário se autentica.
  useEffect(() => {
    // Este useEffect agora é o ponto central para buscar a assinatura.
    // Ele será acionado tanto no login normal quanto na personificação.
    if (authState.isAuthenticated && authState.subscription === null && !authState.isFetchingSubscription) {
      // Define o estado de "buscando" ANTES de iniciar a chamada.
      setAuthState(prevState => ({ ...prevState, isFetchingSubscription: true }));
      fetchAndUpdateSubscription();
    }
  }, [authState.isAuthenticated, authState.subscription, authState.isFetchingSubscription, fetchAndUpdateSubscription]);

  // Reconsulta a assinatura ao retornar para a aplicação (inclusive após testes manuais no banco).
  useEffect(() => {
    if (!authState.isAuthenticated || authState.roles?.includes('super_admin')) return undefined;

    const refreshOnFocus = () => fetchAndUpdateSubscription({ silent: true });
    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, [authState.isAuthenticated, authState.roles, fetchAndUpdateSubscription]);
  // Atualiza o modo de acesso quando a tolerância termina durante uma sessão aberta.
  useEffect(() => {
    const graceEndsAt = authState.subscription?.grace_period_ends_at;
    if (
      authState.subscriptionStatus !== 'past_due'
      || authState.subscription?.access_mode === 'consultorio_only'
      || !graceEndsAt
    ) {
      return undefined;
    }

    const delay = new Date(graceEndsAt).getTime() - Date.now() + 1000;
    const timer = window.setTimeout(() => fetchAndUpdateSubscription({ silent: true }), Math.max(1000, delay));
    return () => window.clearTimeout(timer);
  }, [
    authState.subscription?.access_mode,
    authState.subscription?.grace_period_ends_at,
    authState.subscriptionStatus,
    fetchAndUpdateSubscription,
  ]);
  // --- NOVA LÓGICA PARA isSubscriptionValid ---
  const isSubscriptionValid = useMemo(() => {
    // Se ainda está buscando, não podemos validar.
    if (authState.isFetchingSubscription) {
      return false; // Ou um estado de 'loading' se preferir
    }

    const { subscription, subscriptionStatus } = authState;

    // Se não há assinatura, não é válida.
    if (!subscription) {
      return false;
    }

    // Planos vitalícios são sempre válidos.
    // Para outros planos, verificamos se o status é 'active' ou 'trialing'.
    return subscription.plan?.is_lifetime || ['active', 'trialing', 'past_due'].includes(subscriptionStatus);
  }, [authState.isFetchingSubscription, authState.subscription, authState.subscriptionStatus]);

  const authContextValue = {
    authState,
    isLoggedIn: authState.isAuthenticated,
    loggedInUser: authState.user,
    loginDateTime: authState.loginDateTime,
    currentEntidadeAuth: authState.entidade,
    isSubscriptionValid, // <-- EXPORTA A NOVA VARIÁVEL
    subscriptionStatus: authState.subscriptionStatus,
    isFetchingSubscription: authState.isFetchingSubscription,
    login,
    adminLogin, // Expondo a nova função de login do admin
    handleLogout,
    completeOnboardingStep,
    startImpersonation, // <-- EXPORTA A FUNÇÃO
    stopImpersonation,  // <-- EXPORTA A FUNÇÃO
    isImpersonating: authState.isImpersonating, // <-- EXPORTA O ESTADO
    fetchAndUpdateSubscription,
    updatePartialSubscription, // Expondo a nova função
    setSubscription, // Adicionando a função que faltava
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("\\useAuthContext must be used within an AuthProvider");
  }
  return context;
};